import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

const Ctx = createContext(null);
export const useRadio = () => useContext(Ctx);
const RATES = [1, 1.125, 1.25, 1.5, 2];
export const DEFAULT_LABELS = { hear_it: "Hear it", heading: "Hear it on Range Free Radio", chapter_heading: "Hear it" };

// Site-wide spoken-word player (Range Free Radio). One <audio>, survives route changes.
export const RadioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [episode, setEpisode] = useState(null);
  const [episodes, setEpisodes] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [labels, setLabels] = useState(DEFAULT_LABELS);

  useEffect(() => { axios.get(`${API}/radio/labels`).then((r) => setLabels({ ...DEFAULT_LABELS, ...r.data })).catch(() => {}); }, []);

  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    a.addEventListener("timeupdate", () => setTime(a.currentTime));
    a.addEventListener("durationchange", () => setDuration(a.duration || 0));
    // Voice/music bus: the HOARD player ducks under a voice clip and comes back up when it stops.
    const voice = (on) => { window.__wallVoicePlaying = on; window.dispatchEvent(new CustomEvent("wall:voice", { detail: { on } })); };
    a.addEventListener("play", () => { setPlaying(true); voice(true); });
    a.addEventListener("pause", () => voice(false));
    a.addEventListener("ended", () => voice(false));
    // Browsers allow sound only after the visitor's first tap/click/key — remember it for "play when seen" pins.
    const mark = () => { window.__wallInteracted = true; window.removeEventListener("pointerdown", mark); window.removeEventListener("keydown", mark); };
    window.addEventListener("pointerdown", mark); window.addEventListener("keydown", mark);
    a.addEventListener("pause", () => setPlaying(false));
    a.addEventListener("ended", () => setPlaying(false));
    audioRef.current = a;
    return () => { a.pause(); a.src = ""; };
  }, []);

  const fetchEpisodes = useCallback(async () => {
    try { const r = await axios.get(`${API}/radio/episodes`); setEpisodes(r.data || []); return r.data || []; }
    catch { setEpisodes([]); return []; }
  }, []);

  const load = useCallback((ep, startAt = null, autoplay = true) => {
    const a = audioRef.current;
    if (!a || !ep?.audio_url) return;
    if (episode?.episode_id !== ep.episode_id) {
      a.src = ep.audio_url.startsWith("http") ? ep.audio_url : `${process.env.REACT_APP_BACKEND_URL}${ep.audio_url}`;
      const r0 = RATES.includes(ep.default_rate) ? ep.default_rate : 1;
      a.playbackRate = r0; setRate(r0);
      setEpisode(ep);
      setDuration(ep.duration_sec || 0);
    }
    // startAt null → resume where it was; a number (including 0) → seek there.
    const seekAndPlay = () => { if (startAt != null) a.currentTime = startAt; if (autoplay) a.play().catch(() => {}); };
    if (a.readyState >= 1 || episode?.episode_id === ep.episode_id) seekAndPlay();
    else a.addEventListener("loadedmetadata", seekAndPlay, { once: true });
    setOpen(true); setMinimized(false);
  }, [episode, rate]); // eslint-disable-line react-hooks/exhaustive-deps

  const replaceEpisode = useCallback((ep) => {
    setEpisode(ep);
    setEpisodes((list) => (list || []).map((e) => (e.episode_id === ep.episode_id ? ep : e)));
  }, []);

  const openLatest = useCallback(async () => {
    const list = episodes || await fetchEpisodes();
    if (episode) { setOpen(true); setMinimized(false); return; }
    if (list[0]) load(list[0], null, false);
    else toast("Range Free Radio is tuning up — the first episode lands here soon.", { description: "Admins: run Mirror in the live Admin panel to pull the episodes over." });
  }, [episodes, episode, fetchEpisodes, load]);

  const toggle = useCallback(() => { const a = audioRef.current; if (!a?.src) return; a.paused ? a.play().catch(() => {}) : a.pause(); }, []);
  const seek = useCallback((t) => { const a = audioRef.current; if (a) a.currentTime = Math.max(0, Math.min(t, a.duration || t)); }, []);
  const skip = useCallback((d) => seek((audioRef.current?.currentTime || 0) + d), [seek]);
  const cycleRate = useCallback(() => { const n = RATES[(RATES.indexOf(rate) + 1) % RATES.length]; setRate(n); if (audioRef.current) audioRef.current.playbackRate = n; }, [rate]);
  const close = useCallback(() => { audioRef.current?.pause(); setOpen(false); setMinimized(false); }, []);

  const chapterIndex = useMemo(() => {
    const chs = episode?.chapters || [];
    let i = -1; chs.forEach((c, k) => { if (time >= c.t) i = k; }); return i;
  }, [episode, time]);

  const value = { episode, episodes, fetchEpisodes, playing, time, duration, rate, open, minimized, chapterIndex, labels, setLabels,
    load, openLatest, toggle, seek, skip, cycleRate, close, setMinimized, setOpen, replaceEpisode, RATES };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const fmtTime = (s) => { if (!isFinite(s) || isNaN(s)) return "0:00"; s = Math.floor(s); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60; return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`; };
export const parseChapterText = (text) => {
  const out = []; const re = /(?<!\d)(\d{1,2}:)?(\d{1,2}):(\d{2})(?!\d)/g; const src = (text || "").replace(/&amp;/g, "&");
  const marks = [...src.matchAll(re)];
  marks.forEach((m, i) => {
    const t = (m[1] ? parseInt(m[1]) * 3600 : 0) + parseInt(m[2]) * 60 + parseInt(m[3]);
    const end = i + 1 < marks.length ? marks[i + 1].index : src.length;
    const title = src.slice(m.index + m[0].length, end).replace(/\s+/g, " ").replace(/^[\s:\-–—|]+|[\s:\-–—|]+$/g, "");
    if (title) out.push({ t, title });
  });
  return out.sort((a, b) => a.t - b.t);
};
